import apiClient from "./client";
import type {
  OnboardHotelPayload,
  TaxPolicyPayload,
  RoomRateMappingPayload,
  UpdateBookingEnginePayload,
  UploadIconPayload,
  CreateBrandPayload,
  UpdateBrandPayload,
  BrandSetupPayload,
} from "../pages/metasearch/types";
import type { UpdateConfigPayload } from "../pages/pricing-config/types";

export const api = {
  login: (data: any) => {
    return apiClient.post("/auth/login", data);
  },
  register: (data: any) => {
    return apiClient.post("/auth/register", data);
  },
  forgotPassword: (data: any) => {
    return apiClient.post("/auth/forgot-password", data);
  },
  resetPassword: (data: any) => {
    return apiClient.post("/auth/reset-password", data);
  },
  getUserProfile: (id: string) => {
    return apiClient.get(`/users/${id}`).then((res) => res.data);
  },
  patchPassword: (id: string, data: any) => {
    return apiClient.patch(`/users/${id}/password`, data);
  },
  putUserProfile: (id: string, data: any) => {
    return apiClient.put(`/users/${id}`, data);
  },
  getUserSubscription: (id: string) => {
    return apiClient.get(`/users/${id}/subscriptions`).then((res) => res.data);
  },
  setActiveStatus: (id: string) => {
    return apiClient.post(`/users/${id}/active`);
  },
  getUsers: (data: any) => {
    return apiClient.get(`/users`, { params: data }).then((res) => res.data);
  },
  getLinkedAccounts: (userId: string) => {
    return apiClient
      .get(`/admin/users/${userId}/linked-accounts`)
      .then((res) => res.data);
  },
  linkAccount: (userId: string, otherUserId: string) => {
    return apiClient
      .post(`/admin/users/${userId}/link`, { otherUserId })
      .then((res) => res.data);
  },
  unlinkAccount: (userId: string) => {
    return apiClient
      .delete(`/admin/users/${userId}/link`)
      .then((res) => res.data);
  },
  updateUser: (id: string, isActive: boolean) => {
    return apiClient.patch(`/users/${id}/status`, {
      isActive,
    });
  },
  getStripe: (id: string, tier: "ST" | "PR" | "MX") => {
    return apiClient
      .get(`/users/${id}/stripe-connect-url`, {
        params: {
          redirectUrl: window.location.origin + "/dashboard",
          planType: "M",
          tier: tier,
        },
      })
      .then((res) => res.data);
  },
  patchStripe: (
    id: string,
    tier: "ST" | "PR" | "MX",
    subscriptionId: string
  ) => {
    return apiClient.patch(`/users/${id}/subscriptions/${subscriptionId}`, {
      tier: tier,
      planType: "M",
    });
  },
  customerStripePortal: (id: string) => {
    return apiClient
      .get(`/users/${id}/stripe-customer-portal-url`, {
        params: {
          // redirectUrl: "https://dev.app.stealthseller.co/dashboard",
          // redirectUrl: "http://localhost:3002/dashboard",
          // dynamicRedirectUrl
          redirectUrl: window.location.origin + "/dashboard",
        },
      })
      .then((res) => res.data);
  },
  cancelSubscription: (id: string, subscriptionId: string) => {
    return apiClient
      .delete(`/users/${id}/subscriptions/${subscriptionId}`)
      .then((res) => res.data);
  },
  getUser: (id: string) => {
    return apiClient.get(`/users/${id}`).then((res) => res.data);
  },
  initiateEmailUpdate: (userId: string, email: string) => {
    return apiClient.post(`/users/${userId}/email/update/initiate`, {
      email,
    });
  },
  updateEmail: (userId: string, otp: string) => {
    return apiClient.post(`/users/${userId}/email/update`, {
      otp,
    });
  },
  deleteUser: (userId: string) => {
    return apiClient.delete(`/users/${userId}`);
  },
  getGraph: (sellerId: string, productId: string) => {
    return apiClient.get(`/sellers/${sellerId}/products/${productId}/graph`, {
      responseType: "blob",
    });
  },
  updateUserActivity: (id: string) => {
    return apiClient.post(`/users/${id}/active`);
  },
  saveOnboarding: (userId: string, data: any) => {
    delete data.userPms;
    return apiClient.post(`/users/${userId}/onboarding`, data);
  },
  getOnboarding: (userId: string) => {
    return apiClient.get(`/users/${userId}/onboarding`).then((res) => res.data);
  },
  deleteOnboarding: (userId: string) => {
    return apiClient.delete(`/users/${userId}/onboarding`);
  },
  connectPMS: (
    userId: string,
    data: {
      accessToken: string;
      pms: string;
    }
  ) => {
    const url = `/users/${userId}/pms/connect`;
    return apiClient.post(url, data);
  },
  getMetrics: (
    userId: string,
    data: {
      startDate: string;
      endDate: string;
      type: string;
    }
  ) => {
    const url = `/users/${userId}/metrics?startDate=${data.startDate}&endDate=${data.endDate}&type=${data.type}`;
    return apiClient.get(url).then((res) => res.data);
  },
  getDayMetrics: (userId: string, data: { type: string; date: string }) => {
    const url = `/users/${userId}/day-metrics?type=${data.type}&date=${data.date}`;
    return apiClient.get(url).then((res) => res.data);
  },
  getDayPrice: (userId: string, date: string) => {
    const url = `/users/${userId}/day-price?date=${date}`;
    return apiClient.get(url).then((res) => res.data);
  },
  getHotelData: (userId: string) => {
    const url = `/users/${userId}/hotel-data`;
    return apiClient.get(url).then((res) => res.data);
  },
  saveDayPrice: (userId: string, data: any) => {
    const url = `/users/${userId}/price`;
    return apiClient.post(url, data);
  },
  resetDayPrice: (userId: string, data: any) => {
    const url = `/users/${userId}/price-reset`;
    return apiClient.post(url, data);
  },
  getCalendarData: (userId: string, startDate: string, endDate: string) => {
    const url = `/users/${userId}/calendar-data?startDate=${startDate}&endDate=${endDate}`;
    return apiClient.get(url).then((res) => res.data);
  },
  getDashboardReport: (userId: string) => {
    const url = `/users/${userId}/dashboard`;
    return apiClient.get(url).then((res) => res.data);
  },
  getDashboardCharts: (data: any) => {
    let url = `/users/${data.id}/reports?type=${data.type}`;
    if (data.duration) {
      url += `&duration=${data.duration}`;
    }
    if (data.startDate) {
      url += `&startDate=${data.startDate}`;
    }
    if (data.endDate) {
      url += `&endDate=${data.endDate}`;
    }
    return apiClient.get(url).then((r) => r.data);
  },
  getPredesignedUrl: ({
    id,
    mimeType,
    uploadType,
    fileName,
  }: {
    id: string;
    mimeType: string;
    uploadType: string;
    fileName?: string;
  }) => {
    let url = `/users/${id}/images/signed-url?mimeType=${mimeType}&uploadType=${uploadType}`;
    if (fileName) {
      url += `&fileName=${fileName}`;
    }
    return apiClient.get(url).then((r) => r.data);
  },
  getSignedUrl: (
    id: string,
    params: { uploadType: string; mimeType: string; fileName?: string }
  ) => {
    return apiClient
      .get(`/users/${id}/images/signed-url`, { params })
      .then((res) => res.data);
  },
  // Legacy document endpoints (kept for backwards compatibility)
  uploadDocument: (data: any) => {
    const url = `/documents`;
    return apiClient.post(url, data);
  },
  getDocuments: () => {
    const url = `/documents`;
    return apiClient.get(url).then((r) => r.data);
  },
  deleteDocument: (documentId: string) => {
    const url = `/documents/${documentId}`;
    return apiClient.delete(url);
  },

  // New AI Document Management (RAG) - System-wide documents
  documents: {
    list: () => apiClient.get('/documents/admin').then((res) => res.data),

    upload: (data: {
      userId: string;
      name: string;
      url: string;
      mimeType: string;
      ownerType: 'S';
    }) => apiClient.post('/documents', data).then((res) => res.data),

    regenerate: (id: string) =>
      apiClient.post(`/documents/${id}/regenerate`).then((res) => res.data),

    download: (id: string) =>
      apiClient.get(`/documents/${id}/download`).then((res) => res.data),

    delete: (id: string) =>
      apiClient.delete(`/documents/${id}`).then((res) => res.data),

    getChunks: (id: string, page: number = 1, limit: number = 20) =>
      apiClient
        .get(`/documents/${id}/chunks`, { params: { page, limit } })
        .then((res) => res.data),

    getContent: (id: string) =>
      apiClient
        .get(`/documents/${id}/content`, { responseType: "text", transformResponse: (v) => v })
        .then((res) => res.data as string),

    update: (id: string, data: { content?: string; name?: string }) =>
      apiClient.patch(`/documents/${id}`, data).then((res) => res.data),
  },
  addUser: (data: any) => {
    return apiClient.post("/auth/admin/register", data);
  },

  // Marketing Analytics (for Reporting tab)
  getMarketingAnalytics: (
    userId: string,
    params: { startDate: string; endDate: string }
  ) => {
    return apiClient
      .get(`/users/${userId}/dashboard/googleAds`, { params })
      .then((res) => res.data);
  },

  // Campaign Management
  campaigns: {
    list: (params?: {
      userId?: string;
      status?: string;
      page?: number;
      limit?: number;
    }) => apiClient.get("/admin/campaigns", { params }).then((res) => res.data),

    get: (id: string) =>
      apiClient.get(`/admin/campaigns/${id}`).then((res) => res.data),

    create: (data: {
      userId: string;
      campaigns: Array<{
        name: string;
        type: "brand" | "competitor" | "remarketing";
        dailyBudget: number;
        biddingStrategy?: string;
        targetCpa?: number;
        targetRoas?: number;
        generatedConfig?: any;
      }>;
    }) => apiClient.post("/admin/campaigns", data).then((res) => res.data),

    update: (
      id: string,
      data: {
        name?: string;
        dailyBudget?: number;
        biddingStrategy?: string;
        targetCpa?: number;
        targetRoas?: number;
        generatedConfig?: any;
      }
    ) => apiClient.put(`/admin/campaigns/${id}`, data).then((res) => res.data),

    updateStatus: (
      id: string,
      status: string,
      errorMessage?: string
    ) =>
      apiClient
        .put(`/admin/campaigns/${id}/status`, { status, errorMessage })
        .then((res) => res.data),

    deploy: (
      id: string,
      googleAdsCampaignId: string,
      googleAdsBudgetId?: string
    ) =>
      apiClient
        .post(`/admin/campaigns/${id}/deploy`, {
          googleAdsCampaignId,
          googleAdsBudgetId,
        })
        .then((res) => res.data),

    delete: (id: string) =>
      apiClient.delete(`/admin/campaigns/${id}`).then((res) => res.data),

    getStats: () =>
      apiClient.get("/admin/campaigns/stats").then((res) => res.data),

    getUsersWithCampaigns: () =>
      apiClient.get("/admin/campaigns/users").then((res) => res.data),
  },

  // Optimization Recommendations
  recommendations: {
    listPending: (params?: {
      campaignId?: string;
      page?: number;
      limit?: number;
    }) =>
      apiClient
        .get("/admin/campaigns/recommendations/pending", { params })
        .then((res) => res.data),

    get: (id: string) =>
      apiClient
        .get(`/admin/campaigns/recommendations/${id}`)
        .then((res) => res.data),

    getByCampaign: (campaignId: string) =>
      apiClient
        .get(`/admin/campaigns/${campaignId}/recommendations`)
        .then((res) => res.data),

    updateStatus: (
      id: string,
      data: {
        status: "approved" | "rejected" | "applied";
        editedValue?: any;
        adminNotes?: string;
      }
    ) =>
      apiClient
        .put(`/admin/campaigns/recommendations/${id}/status`, data)
        .then((res) => res.data),

    bulkUpdate: (data: {
      recommendationIds: string[];
      status: "approved" | "rejected";
      adminNotes?: string;
    }) =>
      apiClient
        .post("/admin/campaigns/recommendations/bulk-update", data)
        .then((res) => res.data),
  },

  // Commission Billing APIs
  // Customer Success Tickets
  csTickets: {
    list: (params?: {
      status?: string;
      category?: string;
      urgency?: string;
      department?: string;
      search?: string;
      dateFrom?: string;
      dateTo?: string;
      page?: number;
      limit?: number;
    }) =>
      apiClient
        .get('/admin/cs-tickets', { params })
        .then((res) => res.data),

    get: (id: string) =>
      apiClient.get(`/admin/cs-tickets/${id}`).then((res) => res.data),

    updateResponse: (id: string, finalResponse: string) =>
      apiClient
        .put(`/admin/cs-tickets/${id}/response`, { finalResponse })
        .then((res) => res.data),

    regenerate: (id: string, adminContext?: string, additionalInstructions?: string) =>
      apiClient
        .post(`/admin/cs-tickets/${id}/regenerate`, { adminContext, additionalInstructions })
        .then((res) => res.data),

    getCustomerNote: (id: string) =>
      apiClient
        .get(`/admin/cs-tickets/${id}/customer-note`)
        .then((res) => res.data),

    approve: (id: string) =>
      apiClient
        .post(`/admin/cs-tickets/${id}/approve`)
        .then((res) => res.data),

    reject: (id: string, reason: string) =>
      apiClient
        .post(`/admin/cs-tickets/${id}/reject`, { reason })
        .then((res) => res.data),

    escalate: (id: string, department: string) =>
      apiClient
        .post(`/admin/cs-tickets/${id}/escalate`, { department })
        .then((res) => res.data),

    stats: () =>
      apiClient.get('/admin/cs-tickets/stats').then((res) => res.data),
  },

  customerNotes: {
    searchUsers: (q: string, limit?: number) =>
      apiClient
        .get('/admin/customer-notes/search-users', { params: { q, limit } })
        .then((res) => res.data),

    list: (params?: { search?: string; page?: number; limit?: number }) =>
      apiClient
        .get('/admin/customer-notes', { params })
        .then((res) => res.data),

    create: (data: {
      userId?: string;
      userName?: string;
      hotelName?: string;
      note: string;
      emails: string[];
    }) =>
      apiClient
        .post('/admin/customer-notes', data)
        .then((res) => res.data),

    update: (
      id: string,
      data: {
        userId?: string;
        userName?: string;
        hotelName?: string;
        note: string;
        emails?: string[];
      }
    ) =>
      apiClient
        .put(`/admin/customer-notes/${id}`, data)
        .then((res) => res.data),

    delete: (id: string) =>
      apiClient
        .delete(`/admin/customer-notes/${id}`)
        .then((res) => res.data),
  },

  // CS Knowledge Base
  csKb: {
    getStatic: () =>
      apiClient.get('/admin/cs-kb/built-in').then((res) => res.data),

    list: (params?: { category?: string; search?: string; page?: number; limit?: number }) =>
      apiClient
        .get('/admin/cs-kb', { params })
        .then((res) => res.data),

    get: (id: string) =>
      apiClient.get(`/admin/cs-kb/${id}`).then((res) => res.data),

    create: (data: { title: string; content: string; category?: string }) =>
      apiClient.post('/admin/cs-kb', data).then((res) => res.data),

    update: (id: string, data: { title?: string; content?: string; category?: string }) =>
      apiClient.put(`/admin/cs-kb/${id}`, data).then((res) => res.data),

    delete: (id: string) =>
      apiClient.delete(`/admin/cs-kb/${id}`).then((res) => res.data),
  },

  // Validate a promo code exactly as the signup form does (same resolve
  // endpoint). Returns { valid, commissionRate? } for the given scope.
  resolvePromoCode: (
    code: string,
    scope: "pricing" | "marketing",
  ): Promise<{ valid: boolean; commissionRate?: number }> =>
    apiClient
      .get(`/promo-codes/${encodeURIComponent(code)}/resolve`, {
        params: { scope },
      })
      .then((res) => res.data),

  commissionBilling: {
    getBillingList: (month: string, page?: number, limit?: number) =>
      apiClient
        .get("/admin/commission-billing", { params: { month, page, limit } })
        .then((res) => res.data),

    getStripePlans: (limit?: number, cursor?: string) =>
      apiClient
        .get("/admin/commission-billing/stripe-plans", { params: { limit, cursor } })
        .then((res) => res.data),

    getSummary: (month: string) =>
      apiClient
        .get("/admin/commission-billing/summary", { params: { month } })
        .then((res) => res.data),

    getChargePreview: (params: { userId: string; month: string; year: string }) =>
      apiClient
        .get("/admin/commission-billing/preview", { params })
        .then((res) => res.data),

    setCommissionRate: (data: {
      userId: string;
      commissionRate: number;
      billingEmail: string;
      billingType?: 'fixed' | 'commission' | 'hybrid' | 'commission_or_flat';
      displayBasePrice?: number;
      displayCommissionMarkup?: number;
      showOnboardingFee?: boolean;
      isTestUser?: boolean;
      showReputationManagementWaiver?: boolean;
      platformFeeAmount?: number;
      platformFeeDescription?: string;
      /** Contractual floor for commission_or_flat. Recorded only, not charged. */
      minimumFeeAmount?: number;
    }) =>
      apiClient
        .post("/admin/commission-billing/rate", data)
        .then((res) => res.data),

    setAddonConfig: (data: {
      userId: string;
      addonType: string;
      monthlyPrice?: number;
      startDate?: string;
      trialEnabled?: boolean;
      enabled?: boolean;
    }) =>
      apiClient
        .post("/admin/commission-billing/addons", data)
        .then((res) => res.data),

    getInvoiceEmailPreview: (params: {
      userId: string;
      month: number;
      year: number;
      // Mirrors the admin's modal billing choice so a send_invoice projection
      // previews the payable invoice (Pay button + due date) it will actually send.
      collectionMethod?: "charge_automatically" | "send_invoice";
      daysUntilDue?: number;
    }): Promise<{ subject: string; html: string; isProjected: boolean }> =>
      apiClient
        .get("/admin/users/invoice-preview", {
          params,
          // Projecting a pre-charge bill reads Stripe + Google Ads, so give it
          // headroom over apiClient's (absent) default timeout.
          timeout: 30000,
        })
        .then((res) => res.data),

    chargeClient: (data: {
      userId: string;
      month: number;
      year: number;
      customAmount?: number;
      invoiceNote?: string;
      acknowledgeRateMismatch?: boolean;
      sendInvoiceEmail?: boolean;
      acknowledgeRecharge?: boolean;
      collectionMethod?: "charge_automatically" | "send_invoice";
      daysUntilDue?: number;
      totpCode?: string;
    }) =>
      apiClient
        .post("/admin/commission-billing/charge", data)
        .then((res) => res.data),

    // Emailed-invoice billing (AUT-136): books the period's charge and emails
    // the invoice as an advance notice ("nothing has been charged yet"). Takes
    // no amount and no collection method — the endpoint prices the period from
    // the same preview and always bills the card on file, `noticeDays` later.
    // Behind the same TOTP unlock as /charge. Returns the scheduled row.
    scheduleInvoiceNotice: (data: {
      userId: string;
      month: number;
      year: number;
      noticeDays?: number;
    }): Promise<{
      id: string;
      status: "scheduled" | "charged" | "failed" | "cancelled";
      noticeAmount: number;
      chargeAfter: string;
    }> =>
      apiClient
        .post("/admin/commission-billing/schedule-invoice-notice", data)
        .then((res) => res.data),

    // Voids an INVOICED charge's open Stripe invoice (closes the customer's
    // payment page) and frees the period for re-billing.
    voidInvoice: (chargeId: string) =>
      apiClient
        .post(`/admin/commission-billing/void-invoice/${chargeId}`)
        .then((res) => res.data),

    // Sends the standalone invoice email (receipt, or payable invoice with the
    // Stripe pay link). Idempotent — an already-emailed period returns
    // { result: "skipped" }, never a double send.
    sendInvoiceEmail: (data: {
      userId: string;
      month: number;
      year: number;
    }): Promise<{ result: "sent" | "skipped" | "failed" | "already_paid" }> =>
      apiClient
        .post("/admin/users/invoice-email/send", data)
        .then((res) => res.data),

    enrichBillingData: (userIds: string[], month: string) =>
      apiClient
        .post("/admin/commission-billing/enrich", { userIds, month })
        .then((res) => res.data),

    getChargeHistory: (userId: string) =>
      apiClient
        .get(`/admin/commission-billing/history/${userId}`)
        .then((res) => res.data),

    retryCharge: (chargeId: string, totpCode?: string) =>
      apiClient
        .post(`/admin/commission-billing/retry/${chargeId}`, { totpCode })
        .then((res) => res.data),

    resetChargeForRebill: (chargeId: string, totpCode?: string) =>
      apiClient
        .post(`/admin/commission-billing/reset-for-rebill/${chargeId}`, { totpCode })
        .then((res) => res.data),

    syncPaymentMethod: (userId: string) =>
      apiClient
        .post(`/admin/commission-billing/sync-payment-method/${userId}`)
        .then((res) => res.data),

    // Read-only: compare the app commission rate against any Stripe discount.
    reconcileBilling: (userId: string) =>
      apiClient
        .get(`/admin/commission-billing/reconcile/${userId}`)
        .then((res) => res.data),

    reconcileAll: () =>
      apiClient
        .get(`/admin/commission-billing/reconcile-all`)
        .then((res) => res.data),

    // Apply an unambiguous Stripe discount drift into the app commission rate
    // (app stays source of truth; Stripe is never written).
    syncFromStripe: (userId: string) =>
      apiClient
        .post(`/admin/commission-billing/sync/${userId}`)
        .then((res) => res.data),

    searchStripeCustomers: () =>
      apiClient
        .get("/admin/commission-billing/stripe-customers")
        .then((res) => res.data),

    linkStripeCustomer: (data: {
      userId: string;
      stripeCustomerId: string;
    }) =>
      apiClient
        .post("/admin/commission-billing/link-stripe-customer", data)
        .then((res) => res.data),

    chargeBulk: (userIds: string[], month: number, year: number, totpCode?: string) =>
      apiClient.post("/admin/commission-billing/charge-bulk", {
        userIds,
        month: Number(month),
        year: Number(year),
        totpCode,
      }),

    getBulkChargeStatus: (jobId: string) =>
      apiClient.get(`/admin/commission-billing/charge-bulk/${jobId}`),

    getUserSubscriptions: (userId: string) =>
      apiClient.get(`/admin/commission-billing/subscriptions/${userId}`),

    cancelSubscription: (subscriptionId: string) =>
      apiClient.post(`/admin/commission-billing/cancel-subscription/${subscriptionId}`),
  },

  metasearch: {
    getStatus: () =>
      apiClient.get("/admin/metasearch/status").then((res) => res.data),
    getAvailableHotels: () =>
      apiClient.get("/admin/metasearch/available-hotels").then((res) => res.data),
    onboard: (data: OnboardHotelPayload) =>
      apiClient.post("/admin/metasearch/onboard", data).then((res) => res.data),
    toggleFeed: (configId: string, enabled: boolean) =>
      apiClient
        .patch(`/admin/metasearch/${configId}/toggle-feed`, { enabled })
        .then((res) => res.data),
    updateBookingEngine: (configId: string, data: UpdateBookingEnginePayload) =>
      apiClient
        .patch(`/admin/metasearch/${configId}/booking-engine`, data)
        .then((res) => res.data),
    offboard: (configId: string) =>
      apiClient
        .delete(`/admin/metasearch/${configId}`)
        .then((res) => res.data),
    getHotelCenterStatus: (configId: string) =>
      apiClient
        .get(`/admin/metasearch/${configId}/hotel-center-status`)
        .then((res) => res.data),
    verifyProperty: (configId: string) =>
      apiClient
        .post(`/admin/metasearch/${configId}/verify-property`)
        .then((res) => res.data),
    previewXml: (configId: string) =>
      apiClient
        .get(`/admin/metasearch/${configId}/preview-xml`)
        .then((res) => res.data),
    previewPointsOfSale: (configId: string) =>
      apiClient
        .get(`/admin/metasearch/${configId}/preview-points-of-sale`)
        .then((res) => res.data),
    getTaxPolicies: (configId: string) =>
      apiClient
        .get(`/admin/metasearch/${configId}/tax-policies`)
        .then((res) => res.data),
    updateTaxPolicies: (configId: string, taxPolicies: TaxPolicyPayload[]) =>
      apiClient
        .put(`/admin/metasearch/${configId}/tax-policies`, { taxPolicies })
        .then((res) => res.data),
    getRoomRateMappings: (configId: string) =>
      apiClient
        .get(`/admin/metasearch/${configId}/room-rate-mappings`)
        .then((res) => res.data),
    updateRoomRateMappings: (configId: string, roomRateMappings: RoomRateMappingPayload[]) =>
      apiClient
        .put(`/admin/metasearch/${configId}/room-rate-mappings`, { roomRateMappings })
        .then((res) => res.data),
    getRestrictions: (configId: string) =>
      apiClient
        .get(`/admin/metasearch/${configId}/restrictions`)
        .then((res) => res.data),
    syncEnrichment: (configId: string) =>
      apiClient
        .post(`/admin/metasearch/${configId}/sync-enrichment`)
        .then((res) => res.data),
    pushAri: (configId: string) =>
      apiClient
        .post(`/admin/metasearch/${configId}/push-ari`)
        .then((res) => res.data),
    getDiagnostics: (days?: number) =>
      apiClient
        .get("/admin/metasearch/diagnostics", { params: { days } })
        .then((res) => res.data),
    triggerJob: (jobName: string) =>
      apiClient
        .post(`/admin/metasearch/trigger/${jobName}`)
        .then((res) => res.data),

    getIcons: () =>
      apiClient.get("/admin/metasearch/branding/icons").then((res) => res.data),
    uploadIcon: (data: UploadIconPayload) =>
      apiClient
        .post("/admin/metasearch/branding/icons", data)
        .then((res) => res.data),
    getBrands: () =>
      apiClient
        .get("/admin/metasearch/branding/brands")
        .then((res) => res.data),
    createBrand: (data: CreateBrandPayload) =>
      apiClient
        .post("/admin/metasearch/branding/brands", data)
        .then((res) => res.data),
    getBrand: (brandId: string) =>
      apiClient
        .get(`/admin/metasearch/branding/brands/${brandId}`)
        .then((res) => res.data),
    updateBrand: (brandId: string, data: UpdateBrandPayload) =>
      apiClient
        .patch(`/admin/metasearch/branding/brands/${brandId}`, data)
        .then((res) => res.data),
    updateHotelBrand: (configId: string, brandId: string | null) =>
      apiClient
        .patch(`/admin/metasearch/${configId}/brand`, { brandId })
        .then((res) => res.data),
    brandSetup: (configId: string, data: BrandSetupPayload) =>
      apiClient
        .post(`/admin/metasearch/${configId}/brand-setup`, data)
        .then((res) => res.data),
  },

  pricing: {
    getConfig: (userId: string) =>
      apiClient
        .get(`/pricing/users/${userId}/multiplier-config`)
        .then((res) => res.data),

    updateConfig: (userId: string, data: UpdateConfigPayload) =>
      apiClient
        .patch(`/pricing/users/${userId}/multiplier-config`, data)
        .then((res) => res.data),

    resetConfig: (userId: string, data: { scope: string }) =>
      apiClient
        .post(`/pricing/users/${userId}/multiplier-config/reset`, data)
        .then((res) => res.data),

    deleteRule: (userId: string, ruleId: string) =>
      apiClient
        .delete(`/pricing/users/${userId}/multiplier-config/rules/${ruleId}`)
        .then((res) => res.data),

    undoChange: (userId: string, changeId: string) =>
      apiClient
        .post(`/pricing/users/${userId}/multiplier-config/undo`, { changeId })
        .then((res) => res.data),
  },

  semProvisioning: {
    list: (params: {
      status?: string;
      pmsType?: string;
      search?: string;
      setupMode?: "self_serve" | "concierge" | "null";
      page?: number;
      limit?: number;
    }) =>
      apiClient
        .get("/admin/sem-provisioning/status", { params })
        .then((res) => res.data),
    getStatus: (userId: string) =>
      apiClient
        .get(`/admin/sem-provisioning/${userId}/status`)
        .then((res) => res.data),
    trigger: (userId: string) =>
      apiClient
        .post(`/admin/sem-provisioning/${userId}/trigger`)
        .then((res) => res.data),
    reset: (userId: string, step: string) =>
      apiClient
        .post(`/admin/sem-provisioning/${userId}/reset/${step}`)
        .then((res) => res.data),
    complete: (userId: string, step: string) =>
      apiClient
        .post(`/admin/sem-provisioning/${userId}/complete/${step}`)
        .then((res) => res.data),
    retry: (userId: string, step: string) =>
      apiClient
        .post(`/admin/sem-provisioning/${userId}/retry/${step}`)
        .then((res) => res.data),
    competitorKeywords: (userId: string) =>
      apiClient
        .get(`/admin/sem-provisioning/${userId}/competitor-keywords`)
        .then((res) => res.data),
    draftCompetitorKeywords: (userId: string, competitorNames: string[]) =>
      apiClient
        .post(`/admin/sem-provisioning/${userId}/competitor-keywords/draft`, {
          competitorNames,
        })
        .then((res) => res.data),
    pushCompetitorKeywords: (
      userId: string,
      keywords: { keyword: string; matchType: string; competitorName: string }[]
    ) =>
      apiClient
        .post(`/admin/sem-provisioning/${userId}/competitor-keywords/push`, {
          keywords,
        })
        .then((res) => res.data),
    campaignRegeneration: (userId: string) =>
      apiClient
        .get(`/admin/sem-provisioning/${userId}/campaigns/regeneration`)
        .then((res) => res.data),
    regenerateCampaigns: (userId: string) =>
      apiClient
        .post(`/admin/sem-provisioning/${userId}/campaigns/regenerate`)
        .then((res) => res.data),
    refreshBilling: (userId: string) =>
      apiClient
        .post(`/admin/sem-provisioning/${userId}/refresh-billing`)
        .then((res) => res.data),
    confirmTracking: (userId: string) =>
      apiClient
        .post(`/admin/sem-provisioning/${userId}/confirm-tracking`)
        .then((res) => res.data),
    previewConfirmTrackingEmail: (
      userId: string
    ): Promise<{ subject: string; html: string }> =>
      apiClient
        .get(`/admin/sem-provisioning/${userId}/confirm-tracking/email-preview`)
        .then((res) => res.data),
    getBrandAssets: (userId: string) =>
      apiClient
        .get(`/admin/sem-provisioning/${userId}/brand-assets`)
        .then((res) => res.data),
    setBrandAssetBusinessName: (userId: string, businessName: string) =>
      apiClient
        .put(`/admin/sem-provisioning/${userId}/brand-assets/business-name`, {
          businessName,
        })
        .then((res) => res.data),
    uploadBrandAssetLogo: (userId: string, imageBase64: string) =>
      apiClient
        .post(`/admin/sem-provisioning/${userId}/brand-assets/business-logo`, {
          imageBase64,
        })
        .then((res) => res.data),
    addBrandAssetSearchImages: (userId: string, imagesBase64: string[]) =>
      apiClient
        .post(`/admin/sem-provisioning/${userId}/brand-assets/search-images`, {
          imagesBase64,
        })
        .then((res) => res.data),
    removeBrandAssetSearchImage: (userId: string, imageUrl: string) =>
      apiClient
        .delete(`/admin/sem-provisioning/${userId}/brand-assets/search-images`, {
          data: { imageUrl },
        })
        .then((res) => res.data),
  },

  chargeAuth: {
    me: () => apiClient.get("/admin/charge-auth/me").then((r) => r.data),
    enrollStart: () =>
      apiClient.post("/admin/charge-auth/enroll/start").then((r) => r.data),
    enrollConfirm: (code: string) =>
      apiClient
        .post("/admin/charge-auth/enroll/confirm", { code })
        .then((r) => r.data),
    unlock: (code: string) =>
      apiClient
        .post("/admin/charge-auth/unlock", { code })
        .then((r) => r.data),
    list: () => apiClient.get("/admin/charge-auth").then((r) => r.data),
    add: (data: { email: string; isManager?: boolean }) =>
      apiClient.post("/admin/charge-auth", data).then((r) => r.data),
    remove: (id: string) =>
      apiClient.delete(`/admin/charge-auth/${id}`).then((r) => r.data),
    reset: (id: string) =>
      apiClient.post(`/admin/charge-auth/${id}/reset`).then((r) => r.data),
  },
};
